import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'

type AccountAssistant = Database['public']['Tables']['account_assistants']['Row']
type AssistantInsert = Database['public']['Tables']['account_assistants']['Insert']
type AssistantUpdate = Database['public']['Tables']['account_assistants']['Update']

interface CreateAssistantResult {
  success: boolean
  id?: string
}

interface UseAssistantsReturn {
  assistants: AccountAssistant[]
  loading: boolean
  error: string | null
  createAssistant: (data: Omit<AssistantInsert, 'account_id'>) => Promise<CreateAssistantResult>
  updateAssistant: (id: string, data: Partial<AssistantUpdate>) => Promise<boolean>
  deleteAssistant: (id: string) => Promise<boolean>
  toggleAssistantStatus: (id: string, isActive: boolean) => Promise<boolean>
  refetch: () => Promise<void>
}

export const useAssistants = (): UseAssistantsReturn => {
  const { user } = useAuth()
  const [assistants, setAssistants] = useState<AccountAssistant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAssistants = async (): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      console.log('📥 [useAssistants] Buscando assistentes para account_id:', user.account_id)

      const { data: assistantsData, error: assistantsError } = await supabase
        .from('account_assistants')
        .select('*')
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })

      if (assistantsError) throw assistantsError

      console.log('📦 [useAssistants] Assistentes carregados:', assistantsData?.map(a => ({
        id: a.id,
        name: a.name,
        prompt_mode: a.prompt_mode,
        default_prompt_size: a.default_prompt?.length || 0,
        voice_tone: a.voice_tone ? 'Tem' : 'Vazio',
        main_objective: a.main_objective ? 'Tem' : 'Vazio'
      })))

      setAssistants(assistantsData || [])
    } catch (err: any) {
      console.error('❌ [useAssistants] Erro ao carregar assistentes:', err)
      setError(err.message || 'Erro ao carregar assistentes')
    } finally {
      setLoading(false)
    }
  }

  const createAssistant = async (data: Omit<AssistantInsert, 'account_id'>): Promise<CreateAssistantResult> => {
    if (!user?.account_id || !supabase) return { success: false }

    try {
      setError(null)
      const { data: newAssistant, error } = await supabase
        .from('account_assistants')
        .insert({ ...data, account_id: user.account_id })
        .select('id')
        .single()

      if (error) throw error

      await fetchAssistants()
      return { success: true, id: newAssistant?.id }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar assistente')
      return { success: false }
    }
  }

  const updateAssistant = async (id: string, data: Partial<AssistantUpdate>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)

      console.log('🔄 [useAssistants] Atualizando assistente:', {
        id,
        account_id: user.account_id,
        campos: Object.keys(data),
        prompt_mode: data.prompt_mode,
        default_prompt_size: data.default_prompt?.length || 0,
        voice_tone: data.voice_tone ? 'Preenchido' : 'Vazio'
      })

      const { error } = await supabase
        .from('account_assistants')
        .update(data)
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) {
        console.error('❌ [useAssistants] Erro ao atualizar:', error)
        throw error
      }

      console.log('✅ [useAssistants] Assistente atualizado, recarregando lista...')
      await fetchAssistants()
      return true
    } catch (err: any) {
      console.error('❌ [useAssistants] Exceção ao atualizar:', err)
      setError(err.message || 'Erro ao atualizar assistente')
      return false
    }
  }

  const deleteAssistant = async (id: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_assistants')
        .delete()
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      await fetchAssistants()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar assistente')
      return false
    }
  }

  const toggleAssistantStatus = async (id: string, isActive: boolean): Promise<boolean> => {
    return await updateAssistant(id, { is_active: isActive })
  }

  const refetch = async (): Promise<void> => {
    await fetchAssistants()
  }

  useEffect(() => {
    if (user?.account_id) {
      fetchAssistants()
    }
  }, [user?.account_id])

  return {
    assistants,
    loading,
    error,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    toggleAssistantStatus,
    refetch,
  }
}